import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

const PricingCard = ({
  id,
  name,
  subtitle,
  price,
  priceLabel,
  unitLabel,
  badge,
  disabled,
  billing,
  billedText,
  founding,
}) => {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()

  const subStatus = user?.subscription?.status
  const isPaid = subStatus === 'active' || subStatus === 'past_due'
  const isCurrentPlan = isPaid && user?.subscription?.tier === id

  const ctaLabel = !isAuthenticated
    ? 'Start Free Trial'
    : isCurrentPlan
      ? 'Current Plan'
      : isPaid
        ? 'Change Plan'
        : 'Purchase'

  const ctaDisabled = disabled || isCurrentPlan

  const handleClick = () => {
    if (ctaDisabled) return
    const query = `plan=${id}&billing=${billing || 'monthly'}${founding ? '&founding=1' : ''}`
    if (!isAuthenticated) {
      navigate(`/signup?next=${encodeURIComponent(`/payment?${query}`)}`)
      return
    }
    navigate(`/payment?${query}`)
  }

  // M2.1: the Pro badge gets its own darker green (#2F7F0D) so it reads against the card
  // instead of matching the brand primary everywhere else on the page. Its copy became
  // FOUNDING OFFER in SOW 3 (letter 90 p.3); the colour keys off that string. Figma 29.09
  // `pricing` (spec home-page-arvance-spec.29.09.md R16): COMING SOON is plain #4CBC18.
  const badgeBg =
    badge === 'FOUNDING OFFER'
      ? 'bg-[#2F7F0D] text-white'
      : 'bg-brand-primary text-white'

  // R13 / §6 C1: "/month" is #7D8C74 on Starter and Ultimate and white on Pro, as drawn.
  const unitColor = id === 'pro' ? 'text-white' : 'text-[#7D8C74]'

  // Offsets are card-relative from the 29.09 frame (spec R8-R17); all three cards use the
  // Pro/Ultimate column (§6 D15, D17). Measured values: verification-runs/2026-10-02-pricing-29.09.md.
  return (
    // D2.1.1-new: the whole card is the target, not only the button. `handleClick` already
    // returns early when the card is disabled, so Ultimate stays inert without a second guard
    // here — what it does need is to not offer the pointer cursor, or it advertises an action
    // it will not perform. The CTA keeps its own handler for keyboard users and stops the
    // click there, otherwise a click on the button would fire this one too and navigate twice.
    // Letter 104, 2.1.1-2.1.2: hover lifts 2px (was 4) and draws a 1px #4CBC18 edge (overlay below).
    <div
      onClick={handleClick}
      className={`
        group
        relative
        w-[282px]
        h-[282px]
        rounded-[16px]
        overflow-hidden
        transition-transform duration-300
        ${disabled ? '' : 'hover:-translate-y-[2px]'}
        ${ctaDisabled ? '' : 'cursor-pointer'}
      `}
      style={{ backgroundColor: '#17270D' }}
    >
      {/* The hover edge is an overlay above every layer, not the card's own border: a border would
          take 2px out of the 282 box and move every absolute layer by 1px. */}
      {!disabled && (
        <div
          data-testid="pricing-card-hover-edge"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20 rounded-[16px] border border-transparent transition-colors duration-300 group-hover:border-brand-green"
        />
      )}

      {/* Inner square 258 x 258 at (12, 12), radius 12: fill white 5%, 1px white 10%, shadow blur 5
          black 4% (R8). */}
      <div
        className="absolute top-3 left-3 w-[258px] h-[258px] rounded-[12px] border border-white/10 bg-white/5"
        style={{ boxShadow: '0 0 10px rgba(0,0,0,0.04)' }}
      />

      {/* R9: one #4CBC18 circle, r 26, blur 45.65, centre (234, 86). Replaces the radial gradient and
          the white 5% circle ("Reduce strength of green blur and transparent circle"). */}
      <div
        data-testid="pricing-card-glow"
        aria-hidden="true"
        className="absolute rounded-full pointer-events-none"
        style={{ left: 208, top: 60, width: 52, height: 52, background: '#4CBC18', filter: 'blur(45.65px)' }}
      />

      {/* R16: flush with the inner square's top-right corner, 22 tall, radii top-right and
          bottom-left 8, text 8 from its left edge. */}
      {badge && (
        <div className={`absolute z-10 top-3 right-3 h-[22px] px-2 rounded-tr-[8px] rounded-bl-[8px] flex items-center ${badgeBg}`}>
          <span className="text-[12px] font-bold leading-[22px] whitespace-nowrap">{badge}</span>
        </div>
      )}

      <div className="relative z-10 pl-[36px] pt-[36px] text-left">
        <h3 className="text-[24px] leading-[32px] font-bold text-white">{name}</h3>
        {subtitle && (
          <p className="mt-[5.5px] text-[14px] leading-[20px] text-white">{subtitle}</p>
        )}

        {/* R13: "$" 28/700 sits 4.9 above the number's baseline, "/month" 20/400 2.3 below it and
            8.5 after the number (x 452 / 764 / 1040 in the frame). */}
        <div className="mt-[14.5px] flex items-baseline gap-1 text-white">
          <span className="relative -top-[4.9px] text-[28px] font-bold leading-none">$</span>
          <span className="text-[42px] font-bold leading-none">
            {/* M2.3: the short dash he asked for, not the em dash. */}
            {price ?? priceLabel ?? '-'}
          </span>
          {(price != null || unitLabel) && (
            <span className={`relative top-[2.3px] ml-[4.5px] text-[20px] font-normal leading-none ${unitColor}`}>
              {price != null ? '/month' : unitLabel}
            </span>
          )}
        </div>

        {/* R14: 14/400 white, one line. */}
        {billedText && (
          <p className="mt-[6px] text-[14px] leading-[20px] text-white whitespace-nowrap">{billedText}</p>
        )}
      </div>

      {/* R15/R17: 210 x 56 at (36, 190) whether or not a billed line is drawn above it. Letter 104,
          2.1.3: no hover state of its own -- the card's hover is the one feedback. */}
      <button
        disabled={ctaDisabled}
        onClick={(e) => { e.stopPropagation(); handleClick() }}
        className={`
          absolute z-10 left-[36px] top-[190px] w-[210px] h-[56px] rounded-full text-[14px] font-semibold
          ${
            ctaDisabled
              ? 'bg-[#334629] text-[#67785E] cursor-not-allowed'
              : 'bg-white text-brand-dark'
          }
        `}
      >
        {ctaLabel}
      </button>
    </div>
  )
}

export default PricingCard
